import {
    BlockStack,
    Box,
    Card,
    Checkbox,
    InlineStack,
    Spinner,
    Text,
} from "@shopify/polaris";
import type { ReactNode } from "react";
import { SettingsHelpTooltip } from "./SettingsTooltip";

type SettingsCardProps = {
    title: string;
    description?: string;
    children: ReactNode;
};

type SettingCheckboxProps = {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    description?: string;
    help?: string;
};

const settingRowStyle = {
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: "14px 0",
} as const;

const settingRowTextStyle = {
    flex: "1 1 280px",
    minWidth: 0,
} as const;

const settingRowControlStyle = {
    paddingTop: 2,
} as const;

export const SettingsCard = ({ title, description, children }: SettingsCardProps) => (
    <Card>
        <Box padding="400">
            <BlockStack gap="400">
                <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                        {title}
                    </Text>
                    {description ? (
                        <Text as="p" variant="bodySm" tone="subdued">
                            {description}
                        </Text>
                    ) : null}
                </BlockStack>
                <BlockStack gap="0">{children}</BlockStack>
            </BlockStack>
        </Box>
    </Card>
);

export const SettingCheckbox = ({ label, checked, onChange, description, help }: SettingCheckboxProps) => (
    <div style={settingRowStyle}>
        <div style={settingRowTextStyle}>
            <InlineStack gap="100" blockAlign="center">
                <Text as="span" variant="bodyMd" fontWeight="medium">
                    {label}
                </Text>
                {help ? <SettingsHelpTooltip content={help} /> : null}
            </InlineStack>
            {description ? (
                <Box paddingBlockStart="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                        {description}
                    </Text>
                </Box>
            ) : null}
        </div>
        <div style={settingRowControlStyle}>
            <Checkbox label={label} labelHidden checked={checked} onChange={onChange} />
        </div>
    </div>
);

export const LoadingSettingsCard = ({ label }: { label: string }) => (
    <SettingsCard title={label}>
        <InlineStack gap="200" align="center">
            <Spinner accessibilityLabel={`Loading ${label.toLowerCase()}`} size="small" />
            <Text as="span" variant="bodyMd">
                Loading settings...
            </Text>
        </InlineStack>
    </SettingsCard>
);
